import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Save,
  ShieldCheck,
  KeyRound,
  AlertTriangle,
  Eye,
  EyeOff,
  Trash2,
  CheckCircle2,
  CreditCard,
  RefreshCw,
} from "lucide-react";

const API = "/api";

/**
 * Admin-only page to manage PayMongo API keys SECURELY.
 *
 * Keys are stored in MongoDB (payment_settings.id = "paymongo_keys"),
 * NOT in .env, NOT in chat, NOT in code. The app picks them up instantly
 * on every API call. No restart needed.
 */
export default function AdminPaymongoKeys() {
  const { getAuthHeader } = useAuth();
  const headers = { headers: getAuthHeader() };

  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showPublic, setShowPublic] = useState(false);
  const [newPublic, setNewPublic] = useState("");
  const [newSecret, setNewSecret] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payments/paymongo/admin/keys`, headers);
      setCurrent(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load current keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    const sk = newSecret.trim();
    const pk = newPublic.trim();
    if (!sk || !pk) {
      toast.error("Both keys are required");
      return;
    }
    if (!sk.startsWith("sk_test_") && !sk.startsWith("sk_live_")) {
      toast.error("Secret key must start with sk_test_ or sk_live_");
      return;
    }
    if (!pk.startsWith("pk_test_") && !pk.startsWith("pk_live_")) {
      toast.error("Public key must start with pk_test_ or pk_live_");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/payments/paymongo/admin/keys`,
        { public_key: pk, secret_key: sk },
        headers
      );
      setCurrent(r.data);
      setNewPublic("");
      setNewSecret("");
      setShowSecret(false);
      setShowPublic(false);
      toast.success(
        r.data.test_mode
          ? "Test keys saved. App is now in TEST mode."
          : "🔴 LIVE keys saved. App is now accepting REAL money!"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      const r = await axios.delete(
        `${API}/payments/paymongo/admin/keys`,
        headers
      );
      setCurrent(r.data);
      setConfirmClear(false);
      toast.success("Database keys cleared. Reverted to .env fallback.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const isLive = current && current.test_mode === false && current.configured;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1
          className="text-3xl font-bold text-slate-900 flex items-center gap-2"
          data-testid="admin-paymongo-keys-title"
        >
          <KeyRound className="w-7 h-7 text-emerald-600" /> PayMongo API Keys
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Update your PayMongo Secret + Public keys here. Keys are stored
          securely in the database — never in chat, never in code, never in .env.
          The app uses the new keys the moment you click Save.
        </p>
      </div>

      {/* CURRENT STATE */}
      <Card className={isLive ? "border-2 border-red-400" : "border-2 border-emerald-200"}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Currently in use
            </p>
            {current?.configured ? (
              <span
                className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${
                  isLive
                    ? "bg-red-100 text-red-700"
                    : "bg-amber-100 text-amber-700"
                }`}
                data-testid="admin-paymongo-mode"
              >
                {isLive ? "🔴 LIVE — Real Money" : "🧪 Test Mode"}
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
                Not configured
              </span>
            )}
          </div>

          {current?.configured ? (
            <div className="space-y-2 text-sm font-mono">
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-24 font-sans">Public:</span>
                <span
                  className="text-slate-900 bg-slate-50 px-3 py-1.5 rounded border border-slate-200"
                  data-testid="admin-paymongo-public-preview"
                >
                  {current.public_key_preview || "—"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-24 font-sans">Secret:</span>
                <span
                  className="text-slate-900 bg-slate-50 px-3 py-1.5 rounded border border-slate-200"
                  data-testid="admin-paymongo-secret-preview"
                >
                  {current.secret_key_preview || "—"}
                </span>
              </div>
              <div className="pt-2 text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1 font-sans">
                <span>
                  Source:{" "}
                  <strong className="text-slate-600">
                    {current.source === "database" ? "Database (secure)" : ".env file"}
                  </strong>
                </span>
                {current.updated_at && (
                  <span>
                    Last updated:{" "}
                    <strong className="text-slate-600">
                      {new Date(current.updated_at).toLocaleString()}
                    </strong>
                  </span>
                )}
                {current.updated_by && (
                  <span>
                    By: <strong className="text-slate-600">{current.updated_by}</strong>
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              No keys configured yet. Add them below to start accepting payments.
            </p>
          )}
        </CardContent>
      </Card>

      {/* UPDATE FORM */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
            <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span>
              Keys are validated against PayMongo before saving. Your secret key
              is encrypted in transit (HTTPS) and only YOU (logged-in admin) can
              see this page. Never share these keys with anyone.
            </span>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">
              Public Key
            </label>
            <div className="relative">
              <Input
                type={showPublic ? "text" : "password"}
                value={newPublic}
                onChange={(e) => setNewPublic(e.target.value)}
                placeholder="pk_live_… or pk_test_…"
                className="font-mono pr-10"
                data-testid="admin-paymongo-public-input"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowPublic(!showPublic)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                aria-label="Toggle visibility"
              >
                {showPublic ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">
              Secret Key
            </label>
            <div className="relative">
              <Input
                type={showSecret ? "text" : "password"}
                value={newSecret}
                onChange={(e) => setNewSecret(e.target.value)}
                placeholder="sk_live_… or sk_test_…"
                className="font-mono pr-10"
                data-testid="admin-paymongo-secret-input"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                aria-label="Toggle visibility"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              ⚠️ Treat the secret key like a password. Anyone with this can charge
              your customers and view payouts.
            </p>
          </div>

          {newSecret.startsWith("sk_live_") && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg p-3 text-xs text-red-900">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong>You are about to enable LIVE mode.</strong> Real money
                will be charged from any student who clicks "Select Plan" on the
                subscription page. Make sure you've enabled all needed payment
                methods in PayMongo dashboard first.
              </span>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving || !newSecret || !newPublic}
            className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
            data-testid="admin-paymongo-save"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save and activate new keys
          </Button>
        </CardContent>
      </Card>

      {/* CLEAR ZONE */}
      {current?.configured && current?.source === "database" && (
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-slate-900 flex items-center gap-1.5">
                  <Trash2 className="w-4 h-4 text-slate-500" /> Clear database keys
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Removes the keys from the database. The app will fall back to{" "}
                  <code className="text-[11px] bg-slate-100 px-1 rounded">.env</code>{" "}
                  if any keys are set there, or disable PayMongo otherwise.
                </p>
              </div>
              {!confirmClear ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmClear(true)}
                  data-testid="admin-paymongo-clear-btn"
                >
                  Clear keys
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmClear(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleClear}
                    disabled={saving}
                    data-testid="admin-paymongo-clear-confirm"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Yes, clear
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* PAYMENT METHODS PANEL */}
      <PaymentMethodsPanel headers={headers} configured={!!current?.configured} />

      {/* WEBHOOK SECRET PANEL */}
      <WebhookSecretPanel headers={headers} configured={!!current?.configured} />

      {/* HOW IT WORKS */}
      <Card className="bg-slate-50">
        <CardContent className="p-5 text-xs text-slate-600 space-y-2">
          <p className="font-semibold text-slate-900 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> How this keeps your keys safe
          </p>
          <ul className="space-y-1 list-disc pl-5">
            <li>Keys are stored encrypted in MongoDB and never appear in code, chat, or logs.</li>
            <li>Only admins can see this page — students/tutors get a 403.</li>
            <li>Even on this page, only the <strong>prefix + last 4 characters</strong> are shown (e.g. <code className="bg-white px-1 rounded">sk_live_…G1r</code>).</li>
            <li>To rotate keys: paste new ones above → Save. The app uses them instantly. No deploy, no restart.</li>
            <li>If you ever leak a key, regenerate it in PayMongo dashboard, then paste the new one here.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Webhook Secret Panel ─────────────────────────────────────────────────
function WebhookSecretPanel({ headers, configured }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [input, setInput] = useState("");
  const [show, setShow] = useState(false);

  const load = async () => {
    if (!configured) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payments/paymongo/admin/webhook-secret`, headers);
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load webhook secret status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured]);

  const save = async () => {
    const secret = input.trim();
    if (!secret) {
      toast.error("Paste the webhook secret from PayMongo first");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/payments/paymongo/admin/webhook-secret`,
        { webhook_secret: secret }, headers);
      toast.success("Webhook secret saved — webhooks are now verified ✅");
      setInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear the webhook secret?\nAll incoming webhooks will be REJECTED until a new one is saved.")) return;
    setSaving(true);
    try {
      await axios.put(`${API}/payments/paymongo/admin/webhook-secret`,
        { webhook_secret: "" }, headers);
      toast.success("Webhook secret cleared");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  if (!configured) return null;

  return (
    <Card className="border-2 border-amber-200" data-testid="paymongo-webhook-panel">
      <CardContent className="p-6 space-y-4">
        <div>
          <p className="font-semibold text-slate-900 flex items-center gap-1.5">
            🔐 Webhook Signing Secret
          </p>
          <p className="text-xs text-slate-500 mt-1">
            PayMongo signs every webhook it sends. We verify that signature to prevent
            anyone from forging a fake "payment paid" event and granting themselves credits.
            Required for the webhook route to accept any traffic.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {status?.configured ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs">
                <p className="text-emerald-800">
                  ✅ Configured ({status.source}) · <code className="font-mono">{status.preview}</code>
                </p>
                {status.updated_by && (
                  <p className="text-emerald-700 mt-1">
                    Last updated {status.updated_at ? new Date(status.updated_at).toLocaleString() : "?"} by {status.updated_by}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-900">
                ⚠ <strong>Not configured.</strong> Webhooks are currently REJECTED. Create a webhook
                in PayMongo dashboard → Developers → Webhooks (listening to <code>checkout_session.payment.paid</code>),
                copy the signing secret shown there, and paste it below.
              </div>
            )}

            <div className="space-y-2">
              <div className="flex gap-2 items-stretch">
                <input
                  type={show ? "text" : "password"}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="whsec_..."
                  className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm font-mono"
                  data-testid="paymongo-webhook-input"
                />
                <Button variant="outline" size="sm" type="button" onClick={() => setShow(!show)}>
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex gap-2 justify-end">
                {status?.configured && (
                  <Button variant="outline" size="sm" onClick={clear} disabled={saving}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="paymongo-webhook-clear">
                    <Trash2 className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
                <Button onClick={save} disabled={saving || !input.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="paymongo-webhook-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Secret
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Payment Methods Panel ─────────────────────────────────────────────────
const METHOD_META = {
  card:     { label: "Card (Visa / Mastercard)", icon: "💳" },
  gcash:    { label: "GCash",   icon: "🟢" },
  paymaya:  { label: "Maya",    icon: "🟣" },
  grab_pay: { label: "GrabPay", icon: "🟩" },
};
const ALL_METHODS = ["card", "gcash", "paymaya", "grab_pay"];

function PaymentMethodsPanel({ headers, configured }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);   // null = use auto-discovery, [] = same, [...] = override
  const [mode, setMode] = useState("auto");         // 'auto' | 'override'
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [recentAttempts, setRecentAttempts] = useState([]);

  const load = async () => {
    if (!configured) { setLoading(false); return; }
    setLoading(true);
    try {
      const [infoR, recentR] = await Promise.all([
        axios.get(`${API}/payments/paymongo/admin/payment-methods`, headers),
        axios.get(`${API}/payments/paymongo/admin/recent-checkouts`, headers).catch(() => ({ data: { items: [] } })),
      ]);
      setInfo(infoR.data);
      setRecentAttempts(recentR.data?.items || []);
      if (infoR.data.override && infoR.data.override.length) {
        setMode("override");
        setSelected(infoR.data.override);
      } else {
        setMode("auto");
        setSelected(infoR.data.resolved || ALL_METHODS);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured]);

  const runTestCheckout = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/payments/paymongo/admin/test-checkout`, {}, headers);
      setTestResult(r.data);
      toast.success("Diagnostic checkout complete — see result below");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setTestResult({ error: detail });
      toast.error(`Test failed: ${detail}`);
    } finally {
      setTesting(false);
    }
  };

  const toggle = (m) => {
    setSelected((prev) => (prev || []).includes(m)
      ? (prev || []).filter((x) => x !== m)
      : [...(prev || []), m]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "auto") {
        await axios.put(`${API}/payments/paymongo/admin/payment-methods`,
          { payment_method_types: null }, headers);
        toast.success("Cleared — PayMongo will auto-detect enabled methods.");
      } else {
        if (!selected || selected.length === 0) {
          toast.error("Pick at least one method or switch to Auto-detect");
          setSaving(false);
          return;
        }
        await axios.put(`${API}/payments/paymongo/admin/payment-methods`,
          { payment_method_types: selected }, headers);
        toast.success(`Override saved: ${selected.join(", ")}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!configured) return null;

  return (
    <Card className="border-2 border-indigo-200" data-testid="paymongo-methods-panel">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="font-semibold text-slate-900 flex items-center gap-1.5">
              <CreditCard className="w-4 h-4 text-indigo-600" /> Payment Methods on Checkout
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Controls which payment methods appear on the PayMongo hosted checkout page.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="paymongo-methods-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : !info ? (
          <p className="text-sm text-slate-500 italic">Configure PayMongo keys above first.</p>
        ) : (
          <>
            {/* Discovery diagnostics */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-2">
              <p className="font-medium text-slate-700">🔍 PayMongo reported these methods on your account:</p>
              {info.discovered_raw?.length ? (
                <ul className="space-y-1 font-mono">
                  {info.discovered_raw.map((d, i) => (
                    <li key={i} className="text-slate-600">
                      {d.error ? (
                        <span className="text-amber-700">⚠️ {d.error}: {d.detail || ""}</span>
                      ) : (
                        <span>
                          <strong className="text-slate-900">{d.type || "?"}</strong>
                          <span className="text-slate-400"> · status = </span>
                          <span className={`${d.status === "enabled" || d.status === "active" ? "text-emerald-700" : "text-amber-700"}`}>
                            {d.status || "(none)"}
                          </span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 italic">No data returned by PayMongo's discovery API.</p>
              )}
              <p className="pt-1 text-slate-500">
                <strong>Currently sent to checkout:</strong>{" "}
                <span className="text-emerald-700 font-mono">{(info.resolved || []).join(", ") || "(none)"}</span>
              </p>
            </div>

            {/* Mode selector */}
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setMode("auto")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                  mode === "auto"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid="paymongo-methods-mode-auto"
              >
                Auto-detect (recommended)
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("override");
                  if (!selected || selected.length === 0) setSelected(info.resolved || ALL_METHODS);
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                  mode === "override"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid="paymongo-methods-mode-override"
              >
                Manual override
              </button>
            </div>

            {mode === "override" && (
              <div className="grid grid-cols-2 gap-2" data-testid="paymongo-methods-checklist">
                {ALL_METHODS.map((m) => {
                  const meta = METHOD_META[m];
                  const isOn = (selected || []).includes(m);
                  return (
                    <label
                      key={m}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
                        isOn ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                      data-testid={`paymongo-method-${m}`}
                    >
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggle(m)}
                        className="accent-emerald-600"
                      />
                      <span className="text-lg leading-none">{meta.icon}</span>
                      <span className="text-sm text-slate-700">{meta.label}</span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="paymongo-methods-save"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {mode === "auto" ? "Use Auto-detect" : "Save Override"}
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
              💡 <strong>Tip</strong>: If Visa/Mastercard are activated on your PayMongo dashboard but don't
              show at checkout, click <em>Manual override</em> and tick <strong>Card (Visa/Mastercard)</strong>. Our
              checkout will retry without any method PayMongo rejects, so this is safe.
            </div>

            {/* ── Diagnostic: Test Checkout Session ─────────────────── */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-slate-900 flex items-center gap-1.5">
                    🧪 Test Checkout Session
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Creates a real ₱100 diagnostic session against PayMongo (nobody is charged) and shows the
                    exact payload we sent + PayMongo's raw response. This is the ground truth.
                  </p>
                </div>
                <Button
                  onClick={runTestCheckout}
                  disabled={testing}
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="paymongo-test-checkout"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : "🧪"}
                  Run Diagnostic Checkout
                </Button>
              </div>

              {testResult && (
                <div className="bg-slate-900 text-emerald-200 rounded-md p-3 text-xs font-mono overflow-auto max-h-[400px]" data-testid="paymongo-test-result">
                  {testResult.error ? (
                    <p className="text-rose-300">❌ {testResult.error}</p>
                  ) : (
                    <>
                      <div className="mb-3">
                        <p className="text-emerald-300"><strong>✅ Success:</strong> {String(testResult.success)}</p>
                        <p className="text-emerald-300"><strong>Override active:</strong> {String(testResult.override_active)}
                          {testResult.override_value && ` (${(testResult.override_value || []).join(", ")})`}
                        </p>
                        <p className="text-emerald-300"><strong>Initial resolved methods:</strong> {(testResult.initial_resolved_methods || []).join(", ")}</p>
                        <p className={testResult.final_sent_methods?.includes("card") ? "text-emerald-300" : "text-amber-300"}>
                          <strong>Final methods sent to PayMongo:</strong> {(testResult.final_sent_methods || []).join(", ") || "(none)"}
                        </p>
                        {testResult.dropped_by_paymongo?.length > 0 && (
                          <p className="text-rose-300">
                            <strong>⚠ Dropped by PayMongo:</strong> {testResult.dropped_by_paymongo.join(", ")}
                          </p>
                        )}
                        {testResult.checkout_url && (
                          <p className="mt-2">
                            <a href={testResult.checkout_url} target="_blank" rel="noreferrer" className="text-cyan-300 underline">
                              🔗 Open this diagnostic checkout in a new tab to see the exact UI PayMongo renders
                            </a>
                          </p>
                        )}
                      </div>
                      <details className="text-slate-300">
                        <summary className="cursor-pointer text-slate-400 hover:text-white">Raw attempts (click to expand)</summary>
                        <pre className="mt-2 whitespace-pre-wrap break-all text-[10px] leading-tight">{JSON.stringify(testResult.attempts, null, 2)}</pre>
                      </details>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Recent live checkouts ─────────────────────────────── */}
            {recentAttempts.length > 0 && (
              <div className="border-t pt-4">
                <p className="font-semibold text-slate-900 flex items-center gap-1.5 mb-2">
                  📋 Recent Live Checkout Attempts
                </p>
                <p className="text-xs text-slate-500 mb-2">
                  Shows exactly which payment methods each real checkout session sent to PayMongo (after retry).
                </p>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 text-xs" data-testid="paymongo-recent-attempts">
                  {recentAttempts.map((r) => (
                    <div key={r.id || r.paymongo_checkout_session_id} className="p-2 flex justify-between gap-3 items-start flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <p className="font-medium text-slate-800">{r.student_name || "?"}</p>
                        <p className="text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</p>
                      </div>
                      <div className="flex-1 min-w-[240px]">
                        <p className="text-slate-500 text-[10px] uppercase">Requested → Final</p>
                        <p className="text-slate-700 font-mono text-[10px]">
                          {(r.requested_payment_methods || []).join(", ") || "(none logged)"}
                        </p>
                        <p className={(r.final_payment_methods || []).includes("card") ? "text-emerald-700 font-mono text-[10px]" : "text-amber-700 font-mono text-[10px]"}>
                          → {(r.final_payment_methods || []).join(", ") || "(none logged)"}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] ${
                          r.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                          r.payment_status === "pending" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-500"
                        }`}>{r.payment_status || "?"}</span>
                        <p className="text-slate-400 text-[10px] mt-0.5">{r.amount || 0} {r.currency_symbol || ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
